/**
 * <g-card-grid> - Configured card grid
 *
 * Renders a responsive grid of cards from a JS data array. The card interior
 * (title, subtitle, badges with color rules, metric grid, progress bar) is
 * declared via `.cardConfig`; the host doesn't write per-card HTML. Owns
 * pagination, empty/loading states, and card-click events. Filtering and
 * search stay in the host (push filtered data into `.data`).
 *
 * @example
 *   <g-card-grid id="g" page-size="12" empty-text="No companies"></g-card-grid>
 *   <script>
 *     g.cardConfig = {
 *       cols: { default: 1, md: 2, lg: 3 },
 *       title: { field: 'name', classes: 'font-bold' },
 *       subtitle: { field: 'industry' },
 *       badges: [
 *         { field: 'status', colorMap: { Active: 'success', Prospect: 'info' } },
 *         { field: 'leadScore', label: 'Score',
 *           colorRule: [
 *             { when: { gte: 80 }, color: 'success' },
 *             { when: { gte: 60 }, color: 'warning' },
 *             { color: 'error' }
 *           ] }
 *       ],
 *       metrics: [
 *         { field: 'revenue', label: 'Revenue', render: 'currency', currency: 'USD' },
 *         { field: 'employees', label: 'Employees', render: 'number' }
 *       ],
 *       progress: { field: 'capacity', label: 'Capacity', max: 100,
 *         colorRule: [
 *           { when: { gte: 100 }, color: 'error' },
 *           { when: { gte: 80 }, color: 'warning' },
 *           { color: 'success' }
 *         ] },
 *       footer: [
 *         { field: 'location', icon: 'location-dot' }
 *       ]
 *     };
 *     g.data = companies;
 *     g.addEventListener('card-click', e => openDetail(e.detail.row));
 *   </script>
 *
 * Properties:
 *   .cardConfig  card-interior + grid layout config
 *   .data        Array<object> of records
 *
 * Attributes:
 *   page-size    integer (default 24)
 *   empty-text   string (default 'No records found')
 *   loading      boolean attr
 *
 * Events:
 *   card-click   detail: { row, index }
 *   page-change  detail: { page, totalPages }
 */
import { evalPredicate } from '../util/predicate.js';

const ALLOWED_COLORS = new Set([
  'primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info', 'neutral', 'ghost',
]);

const DEFAULT_COLS = { default: 1, md: 2, lg: 3 };

export class GCardGrid extends HTMLElement {
  constructor() {
    super();
    this._data = [];
    this._cardConfig = null;
    this._page = 1;
    this._renderScheduled = false;
  }

  static get observedAttributes() {
    return ['page-size', 'empty-text', 'loading'];
  }

  attributeChangedCallback() { this.scheduleRender(); }

  connectedCallback() {
    this.classList.add('block');
    if (!this._root) {
      this._root = document.createElement('div');
      this.appendChild(this._root);
    }
    this.scheduleRender();
    this.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
  }

  // ── Public API ──

  get data() { return this._data; }
  set data(rs) {
    this._data = Array.isArray(rs) ? rs : [];
    const total = Math.max(1, Math.ceil(this._data.length / this.pageSize));
    if (this._page > total) this._page = total;
    this.scheduleRender();
  }

  get cardConfig() { return this._cardConfig; }
  set cardConfig(c) {
    this._cardConfig = c && typeof c === 'object' ? c : null;
    this.scheduleRender();
  }

  get pageSize() {
    const n = parseInt(this.getAttribute('page-size'), 10);
    return Number.isFinite(n) && n > 0 ? n : 24;
  }

  get emptyText() {
    return this.getAttribute('empty-text') || 'No records found';
  }

  get loading() {
    return this.hasAttribute('loading');
  }

  setPage(page) {
    const totalPages = this._totalPages();
    const next = Math.max(1, Math.min(page, totalPages));
    if (next === this._page) return;
    this._page = next;
    this.scheduleRender();
    this.dispatchEvent(new CustomEvent('page-change', {
      detail: { page: this._page, totalPages }, bubbles: true, composed: true,
    }));
  }

  // ── Internals ──

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _totalPages() {
    return Math.max(1, Math.ceil(this._data.length / this.pageSize));
  }

  _paginated() {
    const start = (this._page - 1) * this.pageSize;
    return this._data.slice(start, start + this.pageSize);
  }

  _render() {
    if (!this._root) return;
    const cfg = this._cardConfig || {};
    const cols = cfg.cols || DEFAULT_COLS;
    const gridClasses = buildGridClasses(cols);

    const totalPages = this._totalPages();
    const pageRows = this._paginated();
    const total = this._data.length;
    const pageStart = total === 0 ? 0 : (this._page - 1) * this.pageSize;
    const pageEnd = Math.min(pageStart + this.pageSize, total);

    let body;
    if (this.loading) {
      body = `<div class="${gridClasses}">${repeat(6, skeletonCard)}</div>`;
    } else if (pageRows.length === 0) {
      body = `<div class="text-center py-16 text-base-content/50">
        <i class="fa-regular fa-inbox text-3xl mb-2 block"></i>
        ${escapeHtml(this.emptyText)}
      </div>`;
    } else {
      const cards = pageRows.map((row, i) => {
        const absoluteIndex = pageStart + i;
        return `<div class="card bg-base-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full" data-row-index="${absoluteIndex}">${renderCard(row, cfg)}</div>`;
      }).join('');
      body = `<div class="${gridClasses}">${cards}</div>`;
    }

    const pagination = total > this.pageSize ? this._renderPagination(total, pageStart, pageEnd, totalPages) : '';

    this._root.innerHTML = `${body}${pagination}`;
  }

  _renderPagination(total, pageStart, pageEnd, totalPages) {
    const range = `Showing ${pageStart + 1}–${pageEnd} of ${total}`;
    return `
      <div class="mt-4 flex justify-between items-center">
        <span class="text-sm text-base-content/60">${escapeHtml(range)}</span>
        <div class="join">
          <button class="join-item btn btn-sm" data-page="prev"${this._page <= 1 ? ' disabled' : ''}>«</button>
          <button class="join-item btn btn-sm">${this._page}</button>
          <button class="join-item btn btn-sm" data-page="next"${this._page >= totalPages ? ' disabled' : ''}>»</button>
        </div>
      </div>
    `;
  }

  _handleClick = (e) => {
    const pageBtn = e.target.closest('[data-page]');
    if (pageBtn && this.contains(pageBtn)) {
      const dir = pageBtn.getAttribute('data-page');
      this.setPage(this._page + (dir === 'next' ? 1 : -1));
      return;
    }
    const card = e.target.closest('[data-row-index]');
    if (card && this.contains(card)) {
      // Don't fire on interactive children
      if (e.target.closest('button, a, input, select, textarea, label')) return;
      const idx = parseInt(card.getAttribute('data-row-index'), 10);
      const row = this._data[idx];
      if (row) {
        this.dispatchEvent(new CustomEvent('card-click', {
          detail: { row, index: idx },
          bubbles: true, composed: true,
        }));
      }
    }
  };
}

// ──────────── Card renderer ────────────

function renderCard(row, cfg) {
  const parts = [];
  parts.push('<div class="card-body p-4 gap-2">');

  // Title + subtitle
  if (cfg.title) parts.push(renderCardTitle(row, cfg.title));
  if (cfg.subtitle) parts.push(renderCardSubtitle(row, cfg.subtitle));

  // Badges row
  if (Array.isArray(cfg.badges) && cfg.badges.length) {
    const badgeHtml = cfg.badges.map(b => renderBadge(row, b)).filter(Boolean).join(' ');
    if (badgeHtml) parts.push(`<div class="flex flex-wrap gap-1.5 mt-1">${badgeHtml}</div>`);
  }

  // Metrics grid (2-col label/value)
  if (Array.isArray(cfg.metrics) && cfg.metrics.length) {
    parts.push(renderMetricsGrid(row, cfg.metrics));
  }

  // Progress bar
  if (cfg.progress) parts.push(renderProgress(row, cfg.progress));

  // Footer items (icon + value pairs)
  if (Array.isArray(cfg.footer) && cfg.footer.length) {
    parts.push(renderFooter(row, cfg.footer));
  }

  parts.push('</div>');
  return parts.join('\n');
}

function renderCardTitle(row, conf) {
  const v = row[conf.field];
  if (v == null || v === '') return '';
  const cls = `card-title text-base ${conf.classes || ''}`.trim();
  return `<h3 class="${escapeHtml(cls)}">${escapeHtml(String(v))}</h3>`;
}

function renderCardSubtitle(row, conf) {
  const v = row[conf.field];
  if (v == null || v === '') return '';
  return `<p class="text-xs text-base-content/60">${escapeHtml(String(v))}</p>`;
}

function renderBadge(row, b) {
  const v = row[b.field];
  if (v == null || v === '') return '';
  const color = resolveColor(v, b);
  const labelPrefix = b.label ? `${escapeHtml(b.label)}: ` : '';
  return `<span class="badge badge-soft badge-sm badge-${color}">${labelPrefix}${escapeHtml(String(v))}</span>`;
}

function renderMetricsGrid(row, metrics) {
  const cells = metrics.map(m => {
    const v = formatValue(row[m.field], m);
    return `<div>
        <div class="text-[10px] uppercase tracking-wider text-base-content/50">${escapeHtml(m.label || m.field)}</div>
        <div class="text-sm font-medium">${v}</div>
      </div>`;
  }).join('');
  const cols = Math.min(metrics.length, 2);
  return `<div class="grid grid-cols-${cols} gap-3 mt-2">${cells}</div>`;
}

function renderProgress(row, conf) {
  const raw = parseFloat(row[conf.field]);
  if (isNaN(raw)) return '';
  const max = conf.max || 100;
  const pct = Math.max(0, Math.min(100, (raw / max) * 100));
  const color = resolveColor(raw, conf);
  const label = conf.label ? `<div class="flex justify-between text-xs text-base-content/60 mb-0.5"><span>${escapeHtml(conf.label)}</span><span>${pct.toFixed(0)}%</span></div>` : '';
  return `<div class="mt-2">
    ${label}
    <progress class="progress progress-${color} w-full" value="${pct}" max="100"></progress>
  </div>`;
}

function renderFooter(row, footer) {
  const items = footer.map(f => {
    const v = row[f.field];
    if (v == null || v === '') return '';
    const icon = f.icon ? `<i class="fa-regular fa-${escapeHtml(f.icon)} text-base-content/50"></i>` : '';
    const val = formatValue(v, f);
    return `<span class="inline-flex items-center gap-1.5 text-xs text-base-content/70">${icon}<span>${val}</span></span>`;
  }).filter(Boolean).join('');
  if (!items) return '';
  return `<div class="flex flex-wrap gap-3 mt-2 pt-2 border-t border-base-200">${items}</div>`;
}

// ──────────── Render-hint formatting ────────────

// Returns SAFE HTML — every text-bearing branch escapes its own input so
// callers can insert via innerHTML without double-escaping (which would
// strip the icons emitted by the boolean branch). Auto-promotes raw
// boolean values from text-mode columns so untagged Boolean schema fields
// also render as check/cross.
function formatValue(v, conf) {
  if (v == null || v === '') return '—';
  const kind = (conf && conf.render) || 'text';
  if (kind === 'text' && (v === true || v === false)) return renderBoolean(v);
  switch (kind) {
    case 'currency': {
      const n = typeof v === 'number' ? v : parseFloat(v);
      if (isNaN(n)) return escapeHtml(String(v));
      try {
        return escapeHtml(new Intl.NumberFormat('en-US', { style: 'currency', currency: conf.currency || 'USD', maximumFractionDigits: 0 }).format(n));
      } catch { return escapeHtml(String(v)); }
    }
    case 'date': {
      const d = new Date(v);
      if (isNaN(d.getTime())) return escapeHtml(String(v));
      return escapeHtml(d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }));
    }
    case 'datetime': {
      const d = new Date(v);
      if (isNaN(d.getTime())) return escapeHtml(String(v));
      return escapeHtml(d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }));
    }
    case 'number': {
      const n = typeof v === 'number' ? v : parseFloat(v);
      if (isNaN(n)) return escapeHtml(String(v));
      return escapeHtml(new Intl.NumberFormat('en-US').format(n));
    }
    case 'percent': {
      const n = typeof v === 'number' ? v : parseFloat(v);
      if (isNaN(n)) return escapeHtml(String(v));
      return escapeHtml((n * 100).toFixed(1) + '%');
    }
    case 'boolean': return renderBoolean(v);
    case 'ref-link': {
      const display = (typeof v === 'object' && v) ? v[conf.displayField || 'name'] : v;
      return escapeHtml(String(display ?? '—'));
    }
    case 'text':
    default: return escapeHtml(String(v));
  }
}

// Boolean cell: green check for truthy, red x for falsy. Mirrors g-table
// so the two widgets stay visually consistent on the same page. Uses inline
// SVG (not Font Awesome) because FA's MutationObserver doesn't reliably
// catch icons injected via innerHTML inside re-render loops — see g-table.
const SVG_CHECK = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><path d="M9 12l2 2 4-4"></path></svg>`;
const SVG_XMARK = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><path d="M9 9l6 6"></path><path d="M15 9l-6 6"></path></svg>`;

function renderBoolean(v) {
  if (v == null || v === '') return '—';
  const truthy = v === true || v === 1 || v === '1' || v === 'true' || v === 'yes';
  const falsy = v === false || v === 0 || v === '0' || v === 'false' || v === 'no';
  if (!truthy && !falsy) return escapeHtml(String(v));
  return truthy
    ? `<span class="text-success inline-flex" title="Yes" aria-label="Yes">${SVG_CHECK}</span>`
    : `<span class="text-error inline-flex" title="No" aria-label="No">${SVG_XMARK}</span>`;
}

// ──────────── Color resolvers ────────────

function resolveColor(value, conf) {
  // colorMap: { 'Active': 'success', 'Pending': 'warning' } — strict equality
  if (conf.colorMap && typeof conf.colorMap === 'object') {
    const c = conf.colorMap[value];
    if (c && ALLOWED_COLORS.has(c)) return c;
  }
  // colorRule: array of { when?: predicate, color }, evaluated in order, first match wins
  if (Array.isArray(conf.colorRule)) {
    for (const rule of conf.colorRule) {
      if (!rule.when) {
        // No condition = default catch-all
        if (ALLOWED_COLORS.has(rule.color)) return rule.color;
      } else {
        if (evalPredicate(value, rule.when) && ALLOWED_COLORS.has(rule.color)) return rule.color;
      }
    }
  }
  return 'neutral';
}

// ──────────── Layout helpers ────────────

function buildGridClasses(cols) {
  // cols = { default, sm?, md?, lg?, xl? }
  const out = ['grid', 'gap-4'];
  if (cols.default) out.push(`grid-cols-${cols.default}`);
  if (cols.sm) out.push(`sm:grid-cols-${cols.sm}`);
  if (cols.md) out.push(`md:grid-cols-${cols.md}`);
  if (cols.lg) out.push(`lg:grid-cols-${cols.lg}`);
  if (cols.xl) out.push(`xl:grid-cols-${cols.xl}`);
  return out.join(' ');
}

function repeat(n, fn) {
  let out = '';
  for (let i = 0; i < n; i++) out += fn();
  return out;
}

function skeletonCard() {
  return `<div class="card bg-base-100 shadow-sm h-full">
    <div class="card-body p-4 gap-2">
      <div class="skeleton h-5 w-3/4"></div>
      <div class="skeleton h-3 w-1/2"></div>
      <div class="skeleton h-4 w-full mt-2"></div>
    </div>
  </div>`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-card-grid')) {
  customElements.define('g-card-grid', GCardGrid);
}
